import BaseComponent from '../../../general/js/base-component';
import Handlebars from 'handlebars';
import ComponentsCollection from "../../../general/js/components-collection";

const CONDITION_AND = 'and';

const ACTION_SHOW = 'show';
const ACTION_HIDE = 'hide';
const ACTION_CLASS = 'class';

/**
 * example of data-rule attribute
 *
 * {
 *      elements: "id1,id2:false,id3:concreteValue"
 *      condition: 'and'
 *      action: 'show' // hide, class
 *      class: 'example-class' // if action is class
 *      template: 'hbs-template-name' // insert/remove node instead of show/hide
 * }
 *
 */

export default class ShowRuleComponent extends BaseComponent {

    constructor() {
        super(...arguments);

        this.idAttributeName = 'data-rule-id';
        this.defaultConfig = {
            action: ACTION_SHOW,
            condition: CONDITION_AND
        };

        this._config = null;
        this.elementsValues = {};
        this.lastResult = null;

        this.checkFunction = this.checkFunction.bind(this);

        this.init();
    }

    init() {

        // initial input values
        this.elementsValues = this.getValuableElement().reduce((result, element) => {
            result[this.getElementId(element)] = this.getElementValue(element);
            return result;
        }, {});

        this.getElements().forEach((element) => {
            if (element.tagName === 'INPUT' && element.type === 'text') {
                element.addEventListener('input', this.onElementChange.bind(this));
            } else {
                element.addEventListener('change', this.onElementChange.bind(this));
            }
        });

        this.checkVisibility();
    }

    getConfig() {
        if (this._config === null) {
            this._config = this.getParsedConfig();
        }

        return this._config;
    }

    getElements() {
        let config = this.getConfig();
        return config.elements;
    }

    /**
     * Returns all elements except unchecked radio inputs because their value is useless
     *
     * @returns Array
     */
    getValuableElement() {
        return this.getElements().filter((element) => !this.isUncheckedRadioInput(element));
    }

    isUncheckedRadioInput(element) {
        return element.tagName === 'INPUT' && element.type === 'radio' && element.checked === false;
    }

    getParsedConfig() {
        let configString = this.element.getAttribute('data-rule');
        let parsedConfig = {};
        try {
            parsedConfig = JSON.parse(configString);
        } catch (e) {
            //maybe we pass single string to set element(s) only
            parsedConfig = {elements: configString};
        }

        let elementsString = parsedConfig.elements;
        if (elementsString) {
            let elements = [];
            let elementsConditions = {};
            elementsString.split(',').forEach((elementCondition) => {
                let [elementId, elementValue] = elementCondition.trim().split(':');
                let nodes = [...document.querySelectorAll(`[${this.idAttributeName}="${elementId}"]`)];
                if (nodes.length > 0) {
                    if (nodes.length === 1) {
                        // handle single node
                        let element = nodes[0];
                        if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'SELECT') {
                            elements.push(element);
                        }
                    } else {
                        //handle radio list
                        let name = nodes[0].name;
                        let isValidRadioList = nodes.every((element) => {
                            return element.tagName === 'INPUT' && element.type === 'radio' && element.name === name;
                        });
                        if (isValidRadioList) {
                            nodes.forEach(element => elements.push(element));
                        }
                    }
                    elementsConditions[elementId] = (elementValue === undefined || elementValue === 'true') ? true : ( elementValue === 'false' ? false : elementValue);
                }
            });
            parsedConfig.elementsConditions = elementsConditions;
            if (!elements.length) {
                throw new Error('there is no elements in config');
            }

            if (parsedConfig.template) {
                let templateNode = document.getElementById(parsedConfig.template);
                if (!templateNode) {
                    throw new Error(`there is no templateNode with id ${parsedConfig.template}`);
                }
                parsedConfig.template = Handlebars.compile(templateNode.innerHTML);
            }

            return Object.assign({}, this.defaultConfig, parsedConfig, {elements});
        } else {
            throw new Error('there is no elements section in config');
        }
    }

    getElementId(element) {
        return element.getAttribute(this.idAttributeName);
    }

    getElementValue(element) {
        switch (element.type) {
            case 'checkbox':
                return element.checked;
            default:
                return element.value;
        }
    }

    onElementChange(e) {
        let element = e.target;
        this.elementsValues[this.getElementId(element)] = this.getElementValue(element);
        this.checkVisibility();
    }

    checkFunction(id) {
        return this.elementsValues[id] === this.getConfig().elementsConditions[id];
    }

    checkVisibility() {
        let result = false;

        if (this.getConfig().condition === CONDITION_AND) {
            result = Object.keys(this.elementsValues).every(this.checkFunction);
        } else {
            result = Object.keys(this.elementsValues).some(this.checkFunction);
        }
        if (result !== this.lastResult) {
            this.update(result)
        }
    }

    update(result) {
        this.lastResult = result;
        switch (this.getConfig().action) {
            case ACTION_SHOW:
                this.updateShow(result);
                break;
            case ACTION_HIDE:
                this.updateShow(!result);
                break;
            case ACTION_CLASS:
                this.updateClass(result);
                break;
        }
    }

    updateShow(result) {
        if (this.getConfig().template) {
            this.updatePresence(result);
        } else {
            this.updateVisibility();
        }
    }

    updatePresence(result) {
        if (result) {
            let renderedElement = this.getConfig().template();
            this.element.insertAdjacentHTML('afterBegin', renderedElement);
            ComponentsCollection.getInstance().init(this.element);
        } else {
            ComponentsCollection.getInstance().destroy(this.element);
            this.element.innerHTML = '';
        }
    }

    updateClass(result) {
        if (result) {
            this.element.classList.add(this.getConfig().class);
        } else {
            this.element.classList.remove(this.getConfig().class);
        }
    }

    updateVisibility(result) {
        if (result) {
            this.element.style.display = 'block';
        } else {
            this.element.style.display = 'none';
        }
    }
}



// WEBPACK FOOTER //
// ./app/components/show-rule/js/show-rule.component.js